// src/components/QuoteForm.tsx

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faCarSide,
  faPaperPlane,
  faEdit,
} from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@iconify/react';
import { useIncompleteRegistration } from '../hooks/useIncompleteRegistration';
import { useQuote, QuoteFormData } from '../hooks/useQuote';
import {
  useAddressAutocomplete,
  type AddressSuggestion,
} from '../hooks/useAddressAutocomplete';
import IncompleteRegistrationDialog from './IncompleteRegistrationDialog';
import FloatingAlert from './ui/FloatingAlert';
import useAlert from '../hooks/useAlert';
import moment from 'moment';

interface QuoteFormProps {
  onClose: () => void;
  onSuccess: (recordId?: string) => void;
}

const vehicleTypes = ['Car', 'SUV', 'Truck', 'Motorcycle', 'Other'];

// Sanitización básica para evitar XSS al mostrar datos en el frontend
function escape(str: string) {
  return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const QuoteForm: React.FC<QuoteFormProps> = ({ onClose, onSuccess }) => {
  // Calculate years of experience dynamically
  const yearsInBusiness = moment().diff(moment('2007-01-01'), 'years');

  const [formData, setFormData] = useState<QuoteFormData>({
    name: '',
    email: '',
    phone: '',
    pickupLocation: '',
    deliveryLocation: '',
    vehicleType: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [addressCoordinates, setAddressCoordinates] = useState<{
    pickup?: [number, number];
    delivery?: [number, number];
  }>({});
  const [addressSelected, setAddressSelected] = useState<{
    pickup: boolean;
    delivery: boolean;
  }>({ pickup: false, delivery: false });

  // Use custom hook for quote logic
  const { loading, errors, submitQuote, validateField, clearFieldError } = useQuote();
  const { alert, showError, showSuccess, hideAlert } = useAlert();
  const pickupAutocomplete = useAddressAutocomplete();
  const deliveryAutocomplete = useAddressAutocomplete();

  const {
    loading: checkingIncomplete,
    showDialog,
    continueWithPrevious,
    discardAndCreateNew,
  } = useIncompleteRegistration('quote');

  // Auto-hide alert after 5 seconds
  React.useEffect(() => {
    if (alert?.isVisible) {
      const timeout = setTimeout(() => {
        hideAlert();
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [alert, hideAlert]);

  // Manejar la decisión del diálogo
  const handleContinueWithPrevious = () => {
    const record = continueWithPrevious();
    if (record) {
      setIsUpdating(true);
      setRecordId(record.id);
      setFormData({
        name: record.data.name || '',
        email: record.data.email || '',
        phone: record.data.phone || '',
        pickupLocation: record.data.pickup_location || '',
        deliveryLocation: record.data.delivery_location || '',
        vehicleType: record.data.vehicle_type || '',
      });
    }
  };

  const handleDiscardAndCreateNew = async () => {
    await discardAndCreateNew();
    setIsUpdating(false);
    setRecordId(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      pickupLocation: '',
      deliveryLocation: '',
      vehicleType: '',
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);

    if (name === 'pickupLocation') {
      setAddressCoordinates(prev => ({ ...prev, pickup: undefined }));
      setAddressSelected(prev => ({ ...prev, pickup: false }));
      pickupAutocomplete.fetchSuggestions(value);
    }
    if (name === 'deliveryLocation') {
      setAddressCoordinates(prev => ({ ...prev, delivery: undefined }));
      setAddressSelected(prev => ({ ...prev, delivery: false }));
      deliveryAutocomplete.fetchSuggestions(value);
    }
  };

  const handleBlur = async (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    await validateField(name, value);

    if (name === 'pickupLocation') {
      setTimeout(() => pickupAutocomplete.clearSuggestions(), 150);
    }
    if (name === 'deliveryLocation') {
      setTimeout(() => deliveryAutocomplete.clearSuggestions(), 150);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    hideAlert(); // Clear any existing alerts
    setSubmitted(false); // Reset submitted state

    // Validate that addresses were selected from autocomplete
    if (!addressSelected.pickup) {
      showError(
        'Please select a pickup address from the dropdown suggestions.',
        'Pickup Location Required'
      );
      return;
    }

    if (!addressSelected.delivery) {
      showError(
        'Please select a delivery address from the dropdown suggestions.',
        'Delivery Location Required'
      );
      return;
    }

    const result = await submitQuote(
      formData,
      isUpdating,
      recordId,
      addressCoordinates
    );

    if (result.success) {
      setSubmitted(true); // Mark as submitted to disable button
      pickupAutocomplete.clearSuggestions();
      deliveryAutocomplete.clearSuggestions();
      // Show success alert first
      showSuccess(
        'Quote request submitted successfully! You will be redirected to the confirmation page.',
        'Quote Request Successful'
      );
      // Close modal after showing alert (delay to let user see the message)
      setTimeout(() => {
        // The redirect happens in App.tsx handleFormSuccess
        onSuccess(result.recordId || undefined);
      }, 2000);
    } else if (result.error) {
      showError(
        result.error,
        'Quote Request Failed'
      );
    }
  };

  const handleSuggestionSelect = (
    field: 'pickupLocation' | 'deliveryLocation',
    suggestion: AddressSuggestion
  ) => {
    setFormData(prev => ({ ...prev, [field]: suggestion.label }));
    clearFieldError(field);
    if (field === 'pickupLocation') {
      setAddressCoordinates(prev => ({
        ...prev,
        pickup: suggestion.coordinates,
      }));
      setAddressSelected(prev => ({ ...prev, pickup: true }));
      pickupAutocomplete.clearSuggestions();
    } else {
      setAddressCoordinates(prev => ({
        ...prev,
        delivery: suggestion.coordinates,
      }));
      setAddressSelected(prev => ({ ...prev, delivery: true }));
      deliveryAutocomplete.clearSuggestions();
    }
  };

  return (
    <>
      {showDialog && (
        <IncompleteRegistrationDialog
          type="quote"
          onContinue={continueWithPrevious}
          onDiscard={discardAndCreateNew}
        />
      )}

      <div className="bg-white pt-14 px-4 sm:px-6 pb-6 rounded-xl sm:rounded-2xl shadow-lg max-w-xl w-[92vw] sm:w-full max-h-[calc(100vh-120px)] overflow-y-auto relative">
        {/* Floating alert for success/error messages */}
        <FloatingAlert
          type={alert?.type || 'info'}
          message={alert?.message || ''}
          title={alert?.title}
          isVisible={alert?.isVisible || false}
          onDismiss={hideAlert}
        />
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full text-[#003380] hover:text-[#002660] hover:bg-gray-100 transition-colors"
        >
          <Icon icon="mdi:close" className="w-5 h-5" />
        </button>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>
          {/* Name */}
          <div className="flex flex-col gap-2">
            <label htmlFor="quote-name" className="text-left text-sm font-medium text-gray-700">Full Name *</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faUser} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <input
                id="quote-name"
                type="text"
                name="name"
                placeholder="e.g., John Smith"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.name}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
              />
            </div>
            {errors.name && (
              <span className="text-red-600 text-sm">
                {escape(errors.name)}
              </span>
            )}
          </div>

          {/* Email */}
          <div className="flex flex-col gap-2">
            <label htmlFor="quote-email" className="text-left text-sm font-medium text-gray-700">Email *</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <input
                id="quote-email"
                type="email"
                name="email"
                placeholder="e.g., john@email.com"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.email}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
              />
            </div>
            {errors.email && (
              <span className="text-red-600 text-sm">
                {escape(errors.email)}
              </span>
            )}
          </div>

          {/* Phone */}
          <div className="flex flex-col gap-2">
            <label htmlFor="quote-phone" className="text-left text-sm font-medium text-gray-700">Phone *</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faPhone} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <input
                id="quote-phone"
                type="tel"
                name="phone"
                placeholder="e.g., (800) 555-1234"
                onChange={handleChange}
                onBlur={handleBlur}
                value={formData.phone}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
              />
            </div>
            {errors.phone && (
              <span className="text-red-600 text-sm">
                {escape(errors.phone)}
              </span>
            )}
          </div>

          {/* Pickup Location */}
          <div className="flex flex-col gap-2">
            <label htmlFor="quote-pickup" className="text-left text-sm font-medium text-gray-700">Pickup Location *</label>
            <div className="relative">
              <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <input
                  id="quote-pickup"
                  type="text"
                  name="pickupLocation"
                  placeholder="e.g., 123 Main St, Los Angeles, CA"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={formData.pickupLocation}
                  className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
                  autoComplete="off"
                />
              </div>
              {(pickupAutocomplete.loading || pickupAutocomplete.suggestions.length > 0) && (
                <ul className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                  {pickupAutocomplete.loading && (
                    <li className="px-3 py-2 text-sm text-gray-500">Searching addresses…</li>
                  )}
                  {pickupAutocomplete.suggestions.map(suggestion => (
                    <li key={`${suggestion.label}-${suggestion.source}`}>
                      <button
                        type="button"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleSuggestionSelect('pickupLocation', suggestion);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
                      >
                        <span>{suggestion.label}</span>
                      </button>
                    </li>
                  ))}
                  {!pickupAutocomplete.loading &&
                    pickupAutocomplete.suggestions.length === 0 && (
                      <li className="px-3 py-2 text-sm text-gray-500">
                        Keep typing to see suggestions
                      </li>
                    )}
                </ul>
              )}
            </div>
            {errors.pickupLocation && (
              <span className="text-red-600 text-sm">
                {escape(errors.pickupLocation)}
              </span>
            )}
          </div>

          {/* Delivery Location */}
          <div className="flex flex-col gap-2">
            <label htmlFor="quote-delivery" className="text-left text-sm font-medium text-gray-700">Delivery Location *</label>
            <div className="relative">
              <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <input
                  id="quote-delivery"
                  type="text"
                  name="deliveryLocation"
                  placeholder="e.g., 456 Ocean Dr, Miami, FL"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={formData.deliveryLocation}
                  className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 pr-4 outline-none text-gray-900 placeholder:text-gray-400"
                  autoComplete="off"
                />
              </div>
              {(deliveryAutocomplete.loading || deliveryAutocomplete.suggestions.length > 0) && (
                <ul className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                  {deliveryAutocomplete.loading && (
                    <li className="px-3 py-2 text-sm text-gray-500">Searching addresses…</li>
                  )}
                  {deliveryAutocomplete.suggestions.map(suggestion => (
                    <li key={`${suggestion.label}-${suggestion.source}`}>
                      <button
                        type="button"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleSuggestionSelect('deliveryLocation', suggestion);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
                      >
                        <span>{suggestion.label}</span>
                      </button>
                    </li>
                  ))}
                  {!deliveryAutocomplete.loading &&
                    deliveryAutocomplete.suggestions.length === 0 && (
                      <li className="px-3 py-2 text-sm text-gray-500">
                        Keep typing to see suggestions
                      </li>
                    )}
                </ul>
              )}
            </div>
            {errors.deliveryLocation && (
              <span className="text-red-600 text-sm">
                {escape(errors.deliveryLocation)}
              </span>
            )}
          </div>

          {/* Vehicle Type */}
          <div className="flex flex-col gap-2">
            <label htmlFor="quote-vehicle" className="text-left text-sm font-medium text-gray-700">Vehicle Type *</label>
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <div className="pl-3 sm:pl-4 text-gray-400 flex-shrink-0">
                <FontAwesomeIcon icon={faCarSide} className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <select
                id="quote-vehicle"
                name="vehicleType"
                value={formData.vehicleType}
                onChange={handleChange}
                onBlur={handleBlur}
                className="flex-1 min-h-[44px] sm:min-h-[48px] py-3.5 pl-2 sm:pl-3 outline-none bg-white text-gray-900 rounded-xl border-0 focus:ring-0"
              >
                <option value="">Choose vehicle type</option>
                {vehicleTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            {errors.vehicleType && (
              <span className="text-red-600 text-sm">
                {escape(errors.vehicleType)}
              </span>
            )}
          </div>

          {/* Info Box - FREE Quote */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <div className="text-green-600 mr-3">
                <FontAwesomeIcon icon={faCarSide} size="lg" />
              </div>
              <div>
                <h3 className="text-green-800 font-semibold">
                  FREE Quote Request
                </h3>
                <p className="text-green-700 text-sm">
                  No payment required • {yearsInBusiness} years experience •
                  65,000+ vehicles transported
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full flex items-center justify-center min-h-[48px] sm:min-h-[52px] bg-green-600 text-white py-3 px-4 rounded-xl font-semibold transition-all hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || checkingIncomplete || submitted}
          >
            <FontAwesomeIcon
              icon={isUpdating ? faEdit : faPaperPlane}
              className="mr-2"
            />
            {loading
              ? isUpdating
                ? 'Updating...'
                : 'Sending...'
              : isUpdating
                ? 'Update Quote Request'
                : 'Get Free Quote'}
          </button>

          {/* General error message (fallback) */}
          {errors.general && !alert?.isVisible && (
            <div className="mt-3 text-sm text-red-600">Quote Request Error: {escape(errors.general)}</div>
          )}
        </form>

        {/* Dialog for incomplete registration */}
        {showDialog && (
          <IncompleteRegistrationDialog
            type="quote"
            onContinue={handleContinueWithPrevious}
            onDiscard={handleDiscardAndCreateNew}
            onClose={() => { }}
          />
        )}
      </div>
    </>
  );
};

export default QuoteForm;

// Mejoras realizadas:
// - Validación estricta en frontend con Yup.
// - Sanitización básica al mostrar errores en el frontend para evitar XSS.
// - No mostrar errores técnicos al usuario, solo mensajes genéricos.
// - Verificación de autenticación antes de enviar
